import { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Upload, FileText, AlertCircle, Check, Eye, ChevronDown, ChevronRight, RefreshCw, Loader2, Trash2, FolderOpen, BookOpen, Shield, FileCheck, Copy, X } from 'lucide-react';
import { uploadFile } from 'zite-file-upload-sdk';
import { toast } from 'sonner';
import {
  uploadTrainingDocument, UploadTrainingDocumentOutputType,
  getImportQueue, GetImportQueueOutputType,
  extractDocumentText,
  updateImportItem,
  createContentFromImport,
  bulkUpdateImports,
} from 'zite-endpoints-sdk';

type ImportItem = GetImportQueueOutputType['items'][0];

type UploadQueueItem = {
  id: string;
  file: File;
  status: 'validating' | 'uploading' | 'registering' | 'done' | 'error' | 'duplicate';
  progress: number;
  error?: string;
  documentId?: string;
};

const ALLOWED_EXTS = ['pdf', 'docx', 'doc', 'txt', 'rtf', 'jpg', 'jpeg', 'png', 'mp4', 'mov'];
const MAX_SIZE = 25 * 1024 * 1024;

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extOf(name: string) {
  return name.split('.').pop()?.toLowerCase() || '';
}

export default function UploadImportSection() {
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const [importItems, setImportItems] = useState<ImportItem[]>([]);
  const [counts, setCounts] = useState({ pending: 0, extracted: 0, failed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewItem, setPreviewItem] = useState<ImportItem | null>(null);
  const [editText, setEditText] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [extracting, setExtracting] = useState<string | null>(null);
  const [creating, setCreating] = useState<{ item: ImportItem; destination: string } | null>(null);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkContentType, setBulkContentType] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const processingRef = useRef(false);

  const loadQueue = useCallback(() => {
    setLoading(true);
    getImportQueue({}).then(d => {
      setImportItems(d.items);
      setCounts(d.counts);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  // File validation
  const validateFile = (file: File): string | null => {
    const ext = extOf(file.name);
    if (!ALLOWED_EXTS.includes(ext)) {
      return `Unsupported file type: .${ext}`;
    }
    if (file.size > MAX_SIZE) {
      return `File too large: ${formatSize(file.size)} (max ${formatSize(MAX_SIZE)})`;
    }
    // Check for duplicates in current queue
    const isDupInQueue = uploadQueue.some(q => q.file.name === file.name && q.file.size === file.size && q.status !== 'error');
    if (isDupInQueue) {
      return `Already in upload queue: ${file.name}`;
    }
    return null;
  };

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    if (processingRef.current) return; // Prevent double-clicks
    processingRef.current = true;

    const fileArr = Array.from(files);
    const newItems: UploadQueueItem[] = [];

    for (const file of fileArr) {
      const error = validateFile(file);
      const id = `${file.name}-${file.size}-${Date.now()}`;
      if (error) {
        newItems.push({ id, file, status: 'error', progress: 0, error });
        toast.error(error);
      } else {
        newItems.push({ id, file, status: 'validating', progress: 0 });
      }
    }

    setUploadQueue(prev => [...prev, ...newItems]);

    // Process valid files sequentially
    for (const item of newItems) {
      if (item.status === 'error') continue;

      // Step 1: Upload to storage
      setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'uploading', progress: 20 } : q));
      
      let fileUrl: string;
      try {
        const result = await uploadFile({ data: item.file, filename: item.file.name });
        fileUrl = result.fileUrl;
        setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, progress: 60 } : q));
      } catch (err: any) {
        setUploadQueue(prev => prev.map(q => q.id === item.id ? {
          ...q, status: 'error', progress: 0,
          error: err?.message || 'Upload failed — please retry',
        } : q));
        continue;
      }

      // Step 2: Register in database
      setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'registering', progress: 80 } : q));
      
      try {
        const regResult = await uploadTrainingDocument({
          filename: item.file.name,
          fileUrl,
          fileSize: item.file.size,
          mimeType: item.file.type,
        });

        if (regResult.isDuplicate) {
          setUploadQueue(prev => prev.map(q => q.id === item.id ? {
            ...q, status: 'duplicate', progress: 100,
            documentId: regResult.existingDocumentId,
            error: `Duplicate detected — matches existing document`,
          } : q));
          toast.warning(`Duplicate detected: ${item.file.name}`);
        } else {
          setUploadQueue(prev => prev.map(q => q.id === item.id ? {
            ...q, status: 'done', progress: 100,
            documentId: regResult.documentId,
          } : q));
        }
      } catch (err: any) {
        setUploadQueue(prev => prev.map(q => q.id === item.id ? {
          ...q, status: 'error', progress: 0,
          error: err?.message || 'Registration failed',
        } : q));
      }
    }

    processingRef.current = false;
    // Refresh the import queue
    loadQueue();
  }, [uploadQueue, loadQueue]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) handleFiles(e.target.files);
    e.target.value = ''; // Reset so same file can be re-selected
  }, [handleFiles]);

  // Extract text for a document
  const handleExtract = async (docId: string) => {
    setExtracting(docId);
    try {
      const result = await extractDocumentText({ documentId: docId });
      toast.success(`Extracted ${result.wordCount} words (${result.headingsDetected.length} headings)`);
      loadQueue();
    } catch (err: any) {
      toast.error(err?.message || 'Extraction failed');
    }
    setExtracting(null);
  };

  // Open preview/edit
  const openPreview = (item: ImportItem) => {
    setPreviewItem(item);
    setEditText(item.extractedText || '');
    setEditTitle(item.title);
  };

  const savePreviewEdits = async () => {
    if (!previewItem) return;
    try {
      await updateImportItem({
        documentId: previewItem.id,
        title: editTitle,
        extractedText: editText,
      });
      toast.success('Changes saved');
      setPreviewItem(null);
      loadQueue();
    } catch { toast.error('Failed to save'); }
  };

  // Create content from import
  const handleCreateContent = async (destination: string) => {
    if (!creating) return;
    try {
      const result = await createContentFromImport({
        documentId: creating.item.id,
        destination: destination as any,
        title: creating.item.title,
      });
      toast.success(`Draft ${result.destination} "${result.title}" created`);
      setCreating(null);
      loadQueue();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create content');
    }
  };

  // Bulk operations
  const handleBulkUpdate = async () => {
    if (selectedIds.size === 0) return;
    try {
      const updates: any = { documentIds: Array.from(selectedIds) };
      if (bulkCategory) updates.documentCategory = bulkCategory;
      if (bulkContentType) updates.contentType = bulkContentType;
      await bulkUpdateImports(updates);
      toast.success(`Updated ${selectedIds.size} items`);
      setSelectedIds(new Set());
      setShowBulk(false);
      loadQueue();
    } catch { toast.error('Bulk update failed'); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === importItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(importItems.map(i => i.id)));
    }
  };

  const statusBadge = (s?: string) => {
    switch (s) {
      case 'Pending': return <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-600">Pending Extraction</span>;
      case 'Extracted': return <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">Extracted</span>;
      case 'Failed': return <span className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive">Failed</span>;
      case 'OCR Required': return <span className="text-xs px-2 py-0.5 rounded bg-orange-500/10 text-orange-600">OCR Required</span>;
      default: return <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">{s || 'Unknown'}</span>;
    }
  };

  return (
    <div>
      <h2 className="text-lg font-bold text-foreground mb-4">Upload & Import</h2>

      {/* Drop zone */}
      <label
        className={`block border-2 border-dashed rounded-xl p-8 text-center mb-6 cursor-pointer transition-colors ${
          dragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:border-primary/30'
        }`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm font-medium text-foreground">Drop files here or click to browse</p>
        <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, TXT — up to 25 MB each. Images and video also accepted.</p>
        <input type="file" multiple accept=".pdf,.docx,.doc,.txt,.rtf,.jpg,.jpeg,.png,.mp4,.mov" onChange={handleInput} className="hidden" />
      </label>

      {/* Upload progress queue */}
      {uploadQueue.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-foreground text-sm">Upload Progress</h3>
            <button onClick={() => setUploadQueue([])} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
          </div>
          <div className="space-y-2">
            {uploadQueue.map(item => (
              <div key={item.id} className="border border-border rounded-lg p-3 bg-card">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    {item.status === 'done' ? <Check className="w-4 h-4 text-primary shrink-0" /> :
                     item.status === 'error' ? <AlertCircle className="w-4 h-4 text-destructive shrink-0" /> :
                     item.status === 'duplicate' ? <Copy className="w-4 h-4 text-yellow-500 shrink-0" /> :
                     <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" />}
                    <span className="text-sm font-medium text-foreground truncate">{item.file.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{formatSize(item.file.size)}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${
                    item.status === 'done' ? 'bg-primary/10 text-primary' :
                    item.status === 'error' ? 'bg-destructive/10 text-destructive' :
                    item.status === 'duplicate' ? 'bg-yellow-500/10 text-yellow-600' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {item.status === 'done' ? 'Registered' :
                     item.status === 'error' ? 'Failed' :
                     item.status === 'duplicate' ? 'Duplicate' :
                     item.status === 'uploading' ? 'Uploading…' :
                     item.status === 'registering' ? 'Registering…' : 'Validating…'}
                  </span>
                </div>
                {item.error && (
                  <p className="text-xs text-destructive mt-1">{item.error}</p>
                )}
                {(item.status === 'uploading' || item.status === 'registering' || item.status === 'validating') && (
                  <div className="w-full h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${item.progress}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Import Queue */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-foreground">Import Queue</h3>
            <div className="flex gap-2 text-xs">
              <span className="text-muted-foreground">{counts.total} total</span>
              {counts.pending > 0 && <span className="text-yellow-600">{counts.pending} pending</span>}
              {counts.extracted > 0 && <span className="text-primary">{counts.extracted} extracted</span>}
              {counts.failed > 0 && <span className="text-destructive">{counts.failed} failed</span>}
            </div>
          </div>
          <div className="flex gap-2">
            {selectedIds.size > 0 && (
              <Button size="sm" variant="outline" onClick={() => setShowBulk(true)}>
                Bulk Update ({selectedIds.size})
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={loadQueue}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {importItems.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-border rounded-lg">
            <FolderOpen className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-50" />
            <p className="text-sm text-muted-foreground">Import queue is empty</p>
            <p className="text-xs text-muted-foreground mt-1">Upload files above to populate the queue</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Select all */}
            <div className="flex items-center gap-2 px-3 py-1">
              <input type="checkbox" checked={selectedIds.size === importItems.length && importItems.length > 0} onChange={toggleSelectAll} className="rounded border-border" />
              <span className="text-xs text-muted-foreground">Select all</span>
            </div>

            {importItems.map(item => (
              <div key={item.id} className="border border-border rounded-lg p-4 bg-card">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleSelect(item.id)} className="rounded border-border shrink-0" />
                  <FileText className="w-5 h-5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {statusBadge(item.extractionStatus)}
                      {item.documentCategory && <span className="text-xs text-muted-foreground">{item.documentCategory}</span>}
                      {item.contentType && <span className="text-xs text-muted-foreground">→ {item.contentType}</span>}
                      {item.tags && item.tags.length > 0 && item.tags.map(t => (
                        <span key={t} className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {item.extractionStatus === 'Pending' && (
                      <Button size="sm" variant="outline" disabled={extracting === item.id} onClick={() => handleExtract(item.id)}>
                        {extracting === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileCheck className="w-3 h-3" />}
                        <span className="ml-1 hidden sm:inline">Extract</span>
                      </Button>
                    )}
                    {item.extractionStatus === 'Extracted' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => openPreview(item)}>
                          <Eye className="w-3 h-3" /><span className="ml-1 hidden sm:inline">Preview</span>
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setCreating({ item, destination: '' })}>
                          <BookOpen className="w-3 h-3" /><span className="ml-1 hidden sm:inline">Create</span>
                        </Button>
                      </>
                    )}
                    {item.extractionStatus === 'Failed' && (
                      <Button size="sm" variant="outline" disabled={extracting === item.id} onClick={() => handleExtract(item.id)}>
                        <RefreshCw className={`w-3 h-3 ${extracting === item.id ? 'animate-spin' : ''}`} />
                        <span className="ml-1 hidden sm:inline">Retry</span>
                      </Button>
                    )}
                    {item.fileUrl && (
                      <a href={item.fileUrl} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="ghost"><Eye className="w-3 h-3" /></Button>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Extraction Preview / Edit Dialog */}
      <Dialog open={!!previewItem} onOpenChange={open => { if (!open) setPreviewItem(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Extraction Preview</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-muted-foreground">Document Title</Label>
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-muted-foreground">Extracted Text</Label>
                <span className="text-xs text-muted-foreground">{editText.split(/\s+/).filter(Boolean).length} words</span>
              </div>
              <Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={16} className="font-mono text-xs" />
            </div>
            {previewItem?.fileUrl && (
              <a href={previewItem.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                <Eye className="w-3 h-3" /> View original file
              </a>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewItem(null)}>Cancel</Button>
            <Button onClick={savePreviewEdits}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Content Dialog */}
      <Dialog open={!!creating} onOpenChange={open => { if (!open) setCreating(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Content from "{creating?.item.title}"</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            Choose where to import this document. Content is always created as <strong>Draft</strong> with source provenance retained.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => handleCreateContent('sop')} className="border border-border rounded-lg p-4 text-left hover:border-primary/30 transition-colors">
              <FileText className="w-5 h-5 text-primary mb-2" />
              <p className="text-sm font-medium text-foreground">Draft SOP</p>
              <p className="text-xs text-muted-foreground">Standard Operating Procedure</p>
            </button>
            <button onClick={() => handleCreateContent('policy')} className="border border-border rounded-lg p-4 text-left hover:border-primary/30 transition-colors">
              <Shield className="w-5 h-5 text-primary mb-2" />
              <p className="text-sm font-medium text-foreground">Draft Policy</p>
              <p className="text-xs text-muted-foreground">Company Policy document</p>
            </button>
            <button onClick={() => handleCreateContent('handbook')} className="border border-border rounded-lg p-4 text-left hover:border-primary/30 transition-colors">
              <BookOpen className="w-5 h-5 text-primary mb-2" />
              <p className="text-sm font-medium text-foreground">Handbook Section</p>
              <p className="text-xs text-muted-foreground">Staff Handbook chapter</p>
            </button>
            <button onClick={() => handleCreateContent('module')} className="border border-border rounded-lg p-4 text-left hover:border-primary/30 transition-colors">
              <FolderOpen className="w-5 h-5 text-primary mb-2" />
              <p className="text-sm font-medium text-foreground">Training Module</p>
              <p className="text-xs text-muted-foreground">Draft training content</p>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Update ({selectedIds.size} items)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Document Category</Label>
              <Select value={bulkCategory} onValueChange={setBulkCategory}>
                <SelectTrigger><SelectValue placeholder="Select category…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Training">Training</SelectItem>
                  <SelectItem value="Policy">Policy</SelectItem>
                  <SelectItem value="SOP">SOP</SelectItem>
                  <SelectItem value="HR">HR</SelectItem>
                  <SelectItem value="Health & Safety">Health & Safety</SelectItem>
                  <SelectItem value="General">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Content Type</Label>
              <Select value={bulkContentType} onValueChange={setBulkContentType}>
                <SelectTrigger><SelectValue placeholder="Select type…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Training Course">Training Course</SelectItem>
                  <SelectItem value="SOP">SOP</SelectItem>
                  <SelectItem value="Company Policy">Company Policy</SelectItem>
                  <SelectItem value="Handbook Section">Handbook Section</SelectItem>
                  <SelectItem value="Reference Document">Reference Document</SelectItem>
                  <SelectItem value="Safety Information">Safety Information</SelectItem>
                  <SelectItem value="Compliance Document">Compliance Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulk(false)}>Cancel</Button>
            <Button onClick={handleBulkUpdate} disabled={!bulkCategory && !bulkContentType}>Apply to {selectedIds.size} items</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
